import { useState, useEffect, useCallback } from "react";
import { getStorageUsage } from "../api";

/**
 * Format byte count into a human-readable string.
 * @param {number} bytes
 * @returns {string} e.g. "3.2 MB", "1.1 GB"
 */
export function formatBytes(bytes) {
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = bytes / Math.pow(1024, i);
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[i]}`;
}

/**
 * Hook that returns the current user's storage usage and limits.
 * Provides computed helpers for display and threshold checks.
 */
export default function useStorageUsage() {
  const [usedBytes, setUsedBytes] = useState(0);
  const [limitBytes, setLimitBytes] = useState(0);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const data = await getStorageUsage();
      setUsedBytes(data.used_bytes);
      setLimitBytes(data.limit_bytes);
    } catch (err) {
      // Silently fail — storage bar just won't render
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Auto-refresh when documents change (upload, delete, client delete)
  useEffect(() => {
    const handler = () => refresh();
    window.addEventListener("storage-changed", handler);
    return () => window.removeEventListener("storage-changed", handler);
  }, [refresh]);

  const usedGB = usedBytes / (1024 ** 3);
  const limitGB = limitBytes / (1024 ** 3);
  const percentUsed = limitBytes > 0 ? (usedBytes / limitBytes) * 100 : 0;
  const isNearLimit = percentUsed >= 90;
  const isAtLimit = percentUsed >= 100;

  return {
    usedBytes, limitBytes,
    usedGB, limitGB,
    percentUsed,
    isNearLimit, isAtLimit,
    loading,
    refresh,
  };
}
